// React imports
import React, { useRef, useState } from 'react';
import classNames from 'classnames';

// UI Components
import { Modal, CheckList } from '@codametrix/ui-components';
import { vitalware, commonEnums } from '@codametrix/ui-common';
import { isOpenImprovement } from '../improvements/improvements';
import { Conditional } from '@codametrix/ui-components';
import './styles/index.scss';
const FieldDefinitions = [
  {
    key: `remove_hold`,
    helpText: 'Remove hold status on submit.'
  }
];

type VitalWareModalProps = {
  vitalwareIssues: vitalware.CodeIssueMap[];
  mergeSplitHistoryIssues: AmplifyAPI.AmplifyError[];
  documentImprovementIssues: AmplifyAPI.AmplifyError[];
  holdIssues: AmplifyAPI.AmplifyError[];
  improvements: AmplifyAPI.DocumentImprovement[];
  holdCaseStatus: CMxAPI.HoldCaseResponse;
  onClose: () => void;
  onSubmit: (event: any, comment: string, remove_hold: boolean) => void;
  setActiveMiniTab: (activeTabName: string) => void;
  setUnresolvedDocuments: (value: boolean) => void;
};

type VitalWareModalState = {
  commentLength: number;
  removeHoldSelected: boolean;
};
class VitalWareModal extends React.Component<
  VitalWareModalProps,
  VitalWareModalState
> {
  commentRef: React.RefObject<HTMLTextAreaElement>;

  constructor(props: VitalWareModalProps) {
    super(props);

    this.commentRef = React.createRef();
    this.setCommentLength = this.setCommentLength.bind(this);
    this.handleUnresolvedDocumentsClick =
      this.handleUnresolvedDocumentsClick.bind(this);
    this.handleRemoveHoldChange = this.handleRemoveHoldChange.bind(this);
    this.state = { commentLength: 0, removeHoldSelected: false };
  }

  setCommentLength(commentLength: number) {
    this.setState({
      ...this.state,
      commentLength
    });
  }

  handleUnresolvedDocumentsClick(ev: React.MouseEvent) {
    ev.preventDefault();
    this.props.onClose();
    this.props.setUnresolvedDocuments(true);
    this.props.setActiveMiniTab(commonEnums.MiniTabLabels.IMPROVEMENTS_LABEL);
  }

  handleRemoveHoldChange(event: CustomEvent) {
    event.preventDefault();
    var removeHoldValue = event.detail.value.checkboxes?.remove_hold;
    this.setState({
      ...this.state,
      removeHoldSelected: removeHoldValue
    });
  }

  render(): React.ReactNode {
    const props = this.props;
    const { commentLength } = this.state;
    const unresolvedReasonsCount = props.improvements?.filter(
      (improvement) =>
        improvement?.provider_query &&
        (improvement?.providers?.length === 0 ||
          improvement?.providers?.some(
            (provider) =>
              provider?.provider_query_status ===
              commonEnums.DocumentStatus.OPEN
          ))
    ).length;

    const getErrors = (issues, code) =>
      issues && issues[code] && issues[code][commonEnums.EditorIssueLevel.ERROR]
        ? issues[code][commonEnums.EditorIssueLevel.ERROR]
        : [];

    const someError = props.vitalwareIssues.some((issues) =>
      Object.keys(issues).some((code) => getErrors(issues, code).length > 0)
    );
    const overrideSubmitButtonClasses = classNames([
      'override-submit-button',
      'chakra-primary-button',
      {
        disabled:
          (someError && (commentLength || 0) === 0) ||
          (props.documentImprovementIssues.length > 0 &&
            unresolvedReasonsCount !== 0) ||
          (props.holdIssues.length > 0 && !this.state.removeHoldSelected)
      }
    ]);

    return (
      <div className="top-level-modal">
        <Modal
          closable={true}
          maximize={false}
          onClose={props.onClose}
          title="Can you check these items before submission?">
             <div className="vw-modal">
          <Conditional every={[props.mergeSplitHistoryIssues.length > 0]}>
            <div className="merge_split_history">
              <p className="vw-title">Changes occurred while you are working</p>
              <p className="vw-overline">
                You should close this and refresh the case to be sure everything
                still looks as you expect. Otherwise, with a comment you may
                override these warnings.
              </p>
              <div className="vw-issues-box vw-mergesplit-issues-box">
                {props.mergeSplitHistoryIssues.map((issues, index) => (
                  <div key={`merge-history-issue-${index}]`}>
                    <li className="merge-split-detail"> {issues.message}</li>
                  </div>
                ))}
              </div>
            </div>
          </Conditional>

          <Conditional every={[props.documentImprovementIssues.length > 0]}>
            <>
              <h4 className="vw-title mandatory-error">
                Improvement requests must be resolved
              </h4>
              <p className="vw-subtitle">
                Submission is prevented while requests are open. Please resolve
                the document requests before submitting again.
                {` `}
                <span
                  onClick={this.handleUnresolvedDocumentsClick}
                  className="cmx-link">
                  Manage open document improvement reasons.
                </span>
              </p>

              <div className="vw-issues-section vw-error-issues">
                {props.improvements?.map((improvement, idx) => {
                  const isOpenReason = isOpenImprovement(improvement);
                  const reasonText =
                    isOpenReason === 'NO_PROVIDER'
                      ? 'is missing provider.'
                      : isOpenReason === 'OPEN_REASON'
                      ? ' has an open status and must be closed to proceed.'
                      : '';
                  return (
                    <Conditional
                      some={[
                        isOpenReason === 'NO_PROVIDER',
                        isOpenReason === 'OPEN_REASON'
                      ]}>
                      <li className="vw-issues-text">
                        {`${improvement.code} ${reasonText}`}
                      </li>
                    </Conditional>
                  );
                })}
              </div>
            </>
          </Conditional>
          <Conditional every={[props.holdIssues.length > 0]}>
            <div className="hold-issues">
              <h4
                className={`vw-title ${
                  this.state.removeHoldSelected ? '' : 'mandatory-error'
                }`}>
                Case is in a hold status
              </h4>
              <p className="vw-subtitle">
                Please confirm this case is ready for processing by removing the
                hold status.
              </p>
              <CheckList
                FieldDefinitions={FieldDefinitions}
                value={{ remove_hold: false }}
                onChange={this.handleRemoveHoldChange}
              />
            </div>
          </Conditional>

          <Conditional every={[props.vitalwareIssues?.length > 0]}>
            <>
              <h4 className="vw-title">Warning: Claims Issues</h4>
              <p className="vw-subtitle">
                Are you sure you want to submit the charges with the following
                claims issues?
              </p>
              <div className="vw-issues-box">
                {props.vitalwareIssues.map((issues, index) => (
                  <div key={`claims-issue-${index}]`}>
                    {Object.keys(issues).map((code, codeIdx) => (
                      <div key={`codeIdx`}>
                        <p className="vw-code-title">{code}</p>
                        <ul>
                          {getErrors(issues, code) &&
                            getErrors(issues, code).length > 0 && (
                              <div className="vw-issues-section vw-error-issues">
                                <h3 className="vw-issues-title vw-issues-error">
                                  ERRORS
                                </h3>
                                {getErrors(issues, code).map(
                                  (issue, issIndex) => (
                                    <li
                                      className="vw-issues-text"
                                      key={`claims-issue-errors-${index}-${issIndex}]`}>
                                      {issue.message}
                                    </li>
                                  )
                                )}
                              </div>
                            )}

                          {issues[code][commonEnums.EditorIssueLevel.WARNING] &&
                            issues[code][commonEnums.EditorIssueLevel.WARNING]
                              .length > 0 && (
                              <div className="vw-issues-section vw-warning-issues">
                                <h3 className="vw-issues-title vw-issues-warning">
                                  WARNINGS
                                </h3>
                                {issues[code][
                                  commonEnums.EditorIssueLevel.WARNING
                                ].map((issue, issIndex) => (
                                  <li
                                    className="vw-issues-text"
                                    key={`claims-issue-warnings-${index}-${issIndex}]`}>
                                    {issue.message}
                                  </li>
                                ))}
                              </div>
                            )}
                        </ul>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </>
          </Conditional>
          <Conditional
            some={[
              props.vitalwareIssues?.length > 0,
              props.mergeSplitHistoryIssues?.length > 0,
              props.documentImprovementIssues.length > 0,
              props.holdIssues.length > 0
            ]}>
            <div className="override-controls">
              <textarea
                ref={this.commentRef}
                maxLength={1024}
                onChange={(event) =>
                  this.setCommentLength(event.target.value.length)
                }
                rows={5}
                placeholder={`Enter reason (${
                  someError ? 'required' : 'optional'
                })`}
                className="vw-comment cmx-form-input input"></textarea>
              <p className="vw-comment-count">{commentLength}/1024</p>
              <div className="vw-buttons-group">
                <button
                  onClick={props.onClose}
                  className="chakra-secondary-button override-close-button">
                  No, I'll make changes
                </button>
                <button
                  className={classNames(overrideSubmitButtonClasses)}
                  onClick={(event) =>
                    props.onSubmit(
                      event,
                      this.commentRef.current.value,
                      props.holdIssues.length > 0
                        ? this.state.removeHoldSelected
                        : null
                    )
                  }>
                  Override & Submit
                </button>
              </div>
            </div>
          </Conditional>
          </div>
        </Modal>
      </div>
    );
  }
}

export default VitalWareModal;
